import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const CANDIDATE_COUNT = 10;
const IMG_BASE = "/backgrounds"; // public/backgrounds/<n>.jpg
const EASE = "cubic-bezier(.22,.61,.36,1)";
const DURATION_MS = 600;

const BackgroundCtx = createContext(null);

function DefaultSplash({ loaded, total }) {
  const pct = total ? Math.round((loaded / total) * 100) : 0;
  return (
    <div style={{
      position: "absolute", inset: 0, display: "grid", placeItems: "center",
      background: "rgb(1, 152, 255)", color: "white", pointerEvents: "none"
    }}>
      <div style={{ width: 280, textAlign: "center" }}>
        <img className="logo" style={{ width: 50 }} src="/images/Blue-Head-Logo.png" alt="Logo" />
        <div style={{ marginTop: 25, height: 8, borderRadius: 6, background: "rgba(255,255,255,.2)", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 6, background: "#F9D957", transition: "width 120ms linear" }} />
        </div>
      </div>
    </div>
  );
}

const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export function BackgroundProvider({
  children,
  Splash = DefaultSplash,
  minDelayMs = 1000,
  splashFadeMs = 400
}) {
  const candidates = useMemo(
    () => Array.from({ length: CANDIDATE_COUNT }, (_, i) => `${IMG_BASE}/${i + 1}.jpg`),
    []
  );

  const [images, setImages] = useState([]);
  const [loaded, setLoaded] = useState(0);
  const totalCandidates = candidates.length;
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(0);
    setIsReady(false);
    const startedAt = Date.now();

    const preload = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        const done = (ok) => { if (!cancelled) setLoaded((n) => n + 1); resolve({ ok, src }); };
        img.onload = () => done(true);
        img.onerror = () => done(false);
        img.decoding = "async";
        img.loading = "eager";
        img.src = src;
      });

    (async () => {
      const results = await Promise.allSettled(candidates.map(preload));
      if (cancelled) return;
      const ok = results
        .map(r => (r.status === "fulfilled" ? r.value : null))
        .filter(Boolean).filter(r => r.ok).map(r => r.src);
      setImages(shuffle(ok));
      const elapsed = Date.now() - startedAt;
      const wait = Math.max(0, minDelayMs - elapsed);
      setTimeout(() => !cancelled && setIsReady(true), wait);
    })();

    return () => { cancelled = true; };
  }, [candidates, minDelayMs]);

  const totalOk = images.length;

  // --- Animation state/refs ---
  const [currentIdx, setCurrentIdx] = useState(0);
  useEffect(() => {
    if (totalOk > 0) setCurrentIdx(Math.floor(Math.random() * totalOk));
  }, [totalOk]);

  const aRef = useRef(null);
  const bRef = useRef(null);
  const useAOnTopRef = useRef(true);
  const animatingRef = useRef(false);
  const timeoutRef = useRef(null);
  const queueRef = useRef([]);

  // Ensure body is transparent
  useEffect(() => { document.body.style.background = "transparent"; }, []);

  const clearTimer = () => { if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; } };

  const startSlideTo = (targetIdx, direction = "down") => {
    if (totalOk === 0 || targetIdx == null || targetIdx === currentIdx) return;

    const incomingRef = useAOnTopRef.current ? aRef : bRef;
    const outgoingRef = useAOnTopRef.current ? bRef : aRef;
    const incoming = incomingRef.current;
    const outgoing = outgoingRef.current;

    if (incoming) {
      if (incoming.src !== images[targetIdx]) incoming.src = images[targetIdx];
      incoming.style.transition = "none";
      incoming.style.transform = direction === "down" ? "translate3d(0,100%,0)" : "translate3d(0,-100%,0)";
      incoming.style.opacity = "0.9";
      incoming.style.visibility = "visible";
    }
    if (outgoing) {
      if (!outgoing.src) outgoing.src = images[currentIdx];
      outgoing.style.transition = "none";
      outgoing.style.transform = "translate3d(0,0,0)";
      outgoing.style.opacity = "1";
      outgoing.style.visibility = "visible";
    }

    incoming && incoming.getBoundingClientRect(); // layout flush

    animatingRef.current = true;
    requestAnimationFrame(() => {
      const t = `transform ${DURATION_MS}ms ${EASE}, opacity ${DURATION_MS}ms ${EASE}`;
      if (incoming) {
        incoming.style.transition = t;
        incoming.style.transform = "translate3d(0,0,0)";
        incoming.style.opacity = "1";
      }
      if (outgoing) {
        outgoing.style.transition = t;
        outgoing.style.transform = direction === "down" ? "translate3d(0,-100%,0)" : "translate3d(0,100%,0)";
        outgoing.style.opacity = "0.9";
      }
    });

    const finish = () => {
      incoming?.removeEventListener("transitionend", finish);
      outgoing?.removeEventListener("transitionend", finish);
      setCurrentIdx(targetIdx);
      useAOnTopRef.current = !useAOnTopRef.current;
      animatingRef.current = false;
      clearTimer();
      if (outgoing) {
        outgoing.style.visibility = "hidden";
        outgoing.style.transition = "none";
        outgoing.style.opacity = "1";
      }
      const q = queueRef.current.shift();
      if (q) startSlideTo(randomOtherIndex(), q);
    };

    incoming?.addEventListener("transitionend", finish);
    outgoing?.addEventListener("transitionend", finish);
    clearTimer();
    timeoutRef.current = setTimeout(finish, DURATION_MS + 120);
  };

  const randomOtherIndex = () => {
    if (totalOk <= 1) return currentIdx;
    let r;
    do r = Math.floor(Math.random() * totalOk);
    while (r === currentIdx);
    return r;
  };

  const scrollRandom = (direction) => {
    if (!isReady || totalOk === 0) return;
    const dir = direction === "up" ? "up" : "down";
    if (animatingRef.current) {
      queueRef.current.push(dir);
      return;
    }
    startSlideTo(randomOtherIndex(), dir);
  };

  // Always render layers (zIndex: 0)
  const Layers = (
    <div aria-hidden style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
      <img
        ref={aRef}
        alt=""
        decoding="async"
        fetchPriority="low"
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", willChange: "transform, opacity", backfaceVisibility: "hidden",
          transform: "translate3d(0,0,0)", opacity: 1, visibility: "hidden"
        }}
      />
      <img
        ref={bRef}
        alt=""
        decoding="async"
        fetchPriority="low"
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", willChange: "transform, opacity", backfaceVisibility: "hidden",
          transform: "translate3d(0,0,0)", opacity: 1, visibility: "hidden"
        }}
      />
    </div>
  );

  // ----- Ensure first frame is PAINTED before fading splash -----
  const [hasFirstPaint, setHasFirstPaint] = useState(false);

  useEffect(() => {
    // We can attach the first image as soon as we have any successful image,
    // even BEFORE isReady, because Layers are already mounted.
    if (hasFirstPaint) return;
    if (images.length === 0) return;

    const imgEl = aRef.current;
    if (!imgEl) return;

    const startIdx = currentIdx ?? 0;
    const src = images[startIdx] || images[0];
    if (!src) return;

    const attachAndMark = async () => {
      // Attach src if not already
      if (imgEl.src !== src) imgEl.src = src;

      // Make visible in place
      imgEl.style.visibility = "visible";
      imgEl.style.transform = "translate3d(0,0,0)";
      imgEl.style.opacity = "1";
      imgEl.style.transition = "none";

      // Wait for decode (best-effort)
      try {
        if (imgEl.decode) {
          await imgEl.decode();
        } else {
          // Fallback: if already complete with natural dimensions, we’re fine
          if (!(imgEl.complete && imgEl.naturalWidth > 0)) {
            await new Promise((res) => {
              const done = () => { imgEl.removeEventListener('load', done); imgEl.removeEventListener('error', done); res(); };
              imgEl.addEventListener('load', done);
              imgEl.addEventListener('error', done);
            });
          }
        }
      } catch {
        // ignore decode errors; we’ll still proceed
      }
      // Now we are sure the first frame is paintable
      setHasFirstPaint(true);
    };

    attachAndMark();
  }, [images, currentIdx, hasFirstPaint]);

  // Context value
  const value = useMemo(
    () => ({ images, index: currentIdx, isReady, animating: animatingRef.current, scrollRandom }),
    [images, currentIdx, isReady]
  );

  // Splash overlay: only fade when isReady && hasFirstPaint
  const shouldFade = isReady && hasFirstPaint;
  const [showSplash, setShowSplash] = useState(true);
  const [splashOpacity, setSplashOpacity] = useState(1);

  useEffect(() => {
    if (!shouldFade) return;
    const id = requestAnimationFrame(() => {
      setSplashOpacity(0);
      const t = setTimeout(() => setShowSplash(false), splashFadeMs);
      return () => clearTimeout(t);
    });
    return () => cancelAnimationFrame(id);
  }, [shouldFade, splashFadeMs]);

  const SplashOverlay = showSplash ? (
    <div
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        opacity: splashOpacity,
        transition: `opacity ${splashFadeMs}ms ${EASE}`,
        pointerEvents: splashOpacity === 0 ? "none" : "auto"
      }}
    >
      <Splash loaded={loaded} total={totalCandidates} />
    </div>
  ) : null;

  const AppAboveLayers = <div style={{ position: "relative", zIndex: 1 }}>{children}</div>;

  return (
    <BackgroundCtx.Provider value={value}>
      {createPortal(Layers, document.body)}
      {AppAboveLayers}
      {createPortal(SplashOverlay, document.body)}
    </BackgroundCtx.Provider>
  );
}

export function useBackground() {
  const ctx = useContext(BackgroundCtx);
  if (!ctx) throw new Error("useBackground must be used within <BackgroundProvider>");
  return ctx;
}
